import React from "react";

import { authorizedFetch } from "./boot.js";
import { Card } from "./ui.jsx";
import { BANDS, BandChip } from "./stats_standing.jsx";

// A past sale replayed through the ranking, in the Inglis admin console (DECISIONS.md,
// 26 Sep 2026). Everything comes from admin.stats.history@1, which reads packaged history
// generated from Keystone viewing logs and Inglis sale results: no platform sale, lot or
// vendor stands behind it. Keystone recorded no buyer nominations, so every distinct viewer
// counts here, where the live ranking counts buyers' vets only.

const { useEffect, useMemo, useState } = React;

const POINTS = [
  ["D-7", "7 days out"],
  ["D-3", "3 days out"],
  ["D-1", "Day before"],
  ["saleMorning", "Sale morning"],
  ["ownSession", "Own session"],
];

async function read(path) {
  const response = await authorizedFetch(`/collections/${path}`);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(`request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return body;
}

/** The past sales, or [] when this administrator cannot read history yet. */
export function useHistorySales() {
  const [sales, setSales] = useState([]);
  useEffect(() => {
    let live = true;
    read("admin.stats.history@1/")
      .then((body) => { if (live) setSales(Array.isArray(body.sales) ? body.sales : []); })
      .catch(() => { if (live) setSales([]); });
    return () => { live = false; };
  }, []);
  return sales;
}

const breeding = (row) => [row?.sire, row?.dam].filter(Boolean).join(" × ");
const money = (value) => (typeof value === "number" ? `$${Math.round(value).toLocaleString("en-AU")}` : "—");
const percent = (value) => (typeof value === "number" ? `${Math.round(value * 100)}%` : "—");
// Keystone counted every viewer, so "none" means nobody opened the X-rays at all.
const chip = (band) => <BandChip band={band} label={band === "none" ? "No viewer yet" : undefined}/>;

function result(row) {
  if (row.outcome === "Sold") return `Sold ${money(row.price)}`;
  if (row.outcome === "Passed In") return "Passed in";
  return row.outcome;
}

export function AdminSaleHistory({ sale }) {
  const [vendor, setVendor] = useState("");
  const [at, setAt] = useState("ownSession");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { setVendor(""); }, [sale]);

  useEffect(() => {
    let live = true;
    const params = new URLSearchParams({ sale, at });
    if (vendor) params.set("vendor", vendor);
    setData(null);
    setError(null);
    read(`admin.stats.history@1/?${params}`)
      .then((body) => {
        if (!live) return;
        if (body.sale !== sale || body.at !== at || (body.vendor || "") !== vendor) {
          setError({ status: 0 });
          return;
        }
        setData(body);
      })
      .catch((caught) => { if (live) setError(caught); });
    return () => { live = false; };
  }, [sale, at, vendor]);

  const rows = useMemo(() => (data?.lots || []).slice().sort((a, b) =>
    BANDS.indexOf(a.band) - BANDS.indexOf(b.band)
      || (b.viewers || 0) - (a.viewers || 0)
      || Number(a.lot) - Number(b.lot)), [data]);
  const all = !vendor;
  const columns = all ? 5 : 4;

  return (
    <Card title={data?.name || "Past sale"} subtitle="Replayed from Keystone viewing · Inglis only"
          className="flush admin-standing admin-history">
      <div className="standing-head">
        <div className="standing-controls">
          <label className="admin-standing-pick">
            <span className="qstat-label">Vendor</span>
            <select value={vendor} onChange={(event) => setVendor(event.target.value)} aria-label="Vendor">
              <option value="">All lots</option>
              {(data?.vendors || []).map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <div className="seg" role="group" aria-label="As at">
            {POINTS.map(([id, label]) => (
              <button key={id} type="button" className={at === id ? "on" : ""} aria-pressed={at === id}
                      onClick={() => setAt(id)}>{label}</button>
            ))}
          </div>
        </div>
        <p className="standing-note">
          Every distinct viewer counts here: Keystone recorded no buyer nominations, so this is not
          the buyers’ vets rule the live ranking uses. “Own session” reads each horse at the morning
          of the session it sold in.
        </p>
        {data?.status === "too_early" && (
          <p className="standing-note action">
            <strong>Too early to rank at this point.</strong> Fewer than 30 horses had been opened.
          </p>
        )}
        {error && (
          <p role="alert" className="standing-note action">
            {error.status === 404 ? "This sale’s history is not available to this administrator." : "Sale history could not be loaded."}
          </p>
        )}
        {!data && !error && <p role="status" className="caption">Loading sale history…</p>}
      </div>

      {data && data.summary.length > 0 && (
        <table className="table stack-table history-summary">
          <caption className="caption">How each band sold, whole sale</caption>
          <thead><tr>
            <th>Band</th><th style={{ textAlign: "right" }}>Horses</th>
            <th style={{ textAlign: "right" }}>Clearance</th><th style={{ textAlign: "right" }}>Median price</th>
            <th style={{ textAlign: "right" }}>Withdrawn</th>
          </tr></thead>
          <tbody>
            {data.summary.map((row) => (
              <tr key={row.band}>
                <td data-label="Band">{chip(row.band)}</td>
                <td data-label="Horses" style={{ textAlign: "right" }}>{row.horses}</td>
                <td data-label="Clearance" style={{ textAlign: "right" }}>{percent(row.clearance)}</td>
                <td data-label="Median price" style={{ textAlign: "right" }}>{money(row.medianPrice)}</td>
                <td data-label="Withdrawn" style={{ textAlign: "right" }}>{row.withdrawn}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data && (
        <table className="table stack-table">
          <thead><tr>
            <th>Lot</th><th>Horse</th>{all && <th>Vendor</th>}
            <th className="standing-col">In the sale</th><th>Result</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns} className="stack-full" style={{ textAlign: "center", padding: 36, color: "var(--fg-muted)" }}>
                No lots.
              </td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.lot}>
                <td data-label="Lot">{row.lot}</td>
                <td data-label="Horse">{breeding(row) || "—"}</td>
                {all && <td data-label="Vendor">{row.vendor || "—"}</td>}
                <td data-label="In the sale" className="standing-col">
                  <span className="standing-cell">
                    {chip(row.band)}
                    {row.viewers > 0 && <span className="standing-vets">{row.viewers} viewer{row.viewers === 1 ? "" : "s"}</span>}
                  </span>
                </td>
                <td data-label="Result">{result(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
